import React, { useState, useEffect } from 'react';
import {
  Button,
  Stack,
  Typography,
  Box,
  DialogContent,
  DialogActions,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  Select,
  MenuItem,
} from '@mui/material';
import { Close, Add } from '@mui/icons-material';
import { useSupplier } from '@features/suppliers/hooks/useSupplier';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { useToast } from '@shared/hooks/useToast';
import { LinkProductRequestDTO } from '@infrastructure/suppliers/dto/SupplierDTO';
import { Product } from '@domain/product/models/Product';
import { MinimumOrderQuantityType, Supplier, SupplierProduct } from '@domain/suppliers/models/Supplier';
import {
  coerceMoqToItemsOnTypeSwitch,
  currencySymbol,
  sanitizeMoqItemsInput,
} from '@features/suppliers/components/links/supplierLinkFormUtils';
import SupplierSelectBottomSheet from '@features/suppliers/components/links/SupplierSelectBottomSheet';
import { BottomSheetSize } from '@shared/shell/bottomsheet/useBottomSheet';
import { BottomSheet } from '@shared/shell/bottomsheet/BottomSheet';

const DISPLAY_CURRENCY = 'EUR';

export interface AddSupplierToProductSheetProps {
  product: Product;
  supplier?: Supplier;
  /** When set, form is prefilled and submit updates the link via the same link API. */
  existingLink?: SupplierProduct;
  onClose: () => void;
}

const parseOptionalNumber = (raw: string): number | undefined => {
  const s = raw.trim().replace(',', '.');
  if (!s) return undefined;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : undefined;
};

const buildLinkOptions = (params: {
  productId: string;
  currency: string;
  purchasePrice: string;
  minimumOrderQuantityType: MinimumOrderQuantityType | '';
  minimumOrderQuantity: string;
  orderInMultiplesOf: string;
}): LinkProductRequestDTO | undefined => {

  const opts: LinkProductRequestDTO = { productId: params.productId };

  const price = parseOptionalNumber(params.purchasePrice);
  if (price != null) {
    opts.purchasePrice = price;
    opts.currency = params.currency.trim() || 'EUR';
  }
  const minQty = parseOptionalNumber(params.minimumOrderQuantity);
  if (minQty != null) {
    const t = (params.minimumOrderQuantityType || 'items') as MinimumOrderQuantityType;
    opts.minimumOrderQuantity = t === 'items' ? Math.floor(Math.abs(minQty)) : minQty;
    opts.minimumOrderQuantityType = t;
  }
  const mult = parseOptionalNumber(params.orderInMultiplesOf);
  if (mult != null) opts.orderInMultiplesOf = mult;
  return Object.keys(opts).length > 0 ? opts : undefined;
};

const AddSupplierToProductSheet: React.FC<AddSupplierToProductSheetProps> = ({
  product,
  supplier: initialSupplierProp,
  existingLink,
  onClose,
}) => {
  const { error } = useToast();
  const theme = useTheme();

  const [purchasePrice, setPurchasePrice] = useState('');
  const currencies = ['EUR', 'USD', 'GBP', 'CHF'];
  const [currency, setCurrency] = useState<string>(currencies[0]);
  const [minimumOrderQuantityType, setMinimumOrderQuantityType] = useState<MinimumOrderQuantityType | ''>('items');
  const [minimumOrderQuantity, setMinimumOrderQuantity] = useState('');
  const [orderInMultiplesOf, setOrderInMultiplesOf] = useState('');


  const [supplier, setSupplier] = useState<Supplier | undefined>(initialSupplierProp);
  const { linkProducts, isLinkingProduct } = useSupplier(supplier?.id ?? '');
  const [isSupplierSelectBottomSheetOpen, setIsSupplierSelectBottomSheetOpen] = useState<boolean>(
    !initialSupplierProp && !existingLink
  );

  const sym = currencySymbol(currency || DISPLAY_CURRENCY);

  useEffect(() => {
    if (!existingLink) return;
    setPurchasePrice(existingLink.purchasePrice != null ? String(existingLink.purchasePrice) : '');
    setCurrency(existingLink.currency ?? currencies[0]);
    setMinimumOrderQuantityType(existingLink.minimumOrderQuantityType ?? 'items');
    setMinimumOrderQuantity(
      existingLink.minimumOrderQuantity != null ? String(existingLink.minimumOrderQuantity) : ''
    );
    setOrderInMultiplesOf(
      existingLink.orderInMultiplesOf != null ? String(existingLink.orderInMultiplesOf) : ''
    );
    if (initialSupplierProp) setSupplier(initialSupplierProp);
    setIsSupplierSelectBottomSheetOpen(false);
  }, [existingLink, initialSupplierProp]);

  const handleSubmit = async () => {
    if (!supplier?.id || !product?.id) return;

    try {
      await linkProducts({
        supplierId: supplier.id,
        products: [{
          productId: product.id,
          ...buildLinkOptions({
            productId: product.id,
            currency,
            purchasePrice,
            minimumOrderQuantityType,
            minimumOrderQuantity,
            orderInMultiplesOf,
          }),
        }],
      });
      onClose();
    } catch {
      error(existingLink ? 'Failed to update supplier link' : 'Failed to link supplier');
    }
  };

  const closeSupplierSelectBottomSheet = () => {

    if (!supplier) {
      onClose();
      return;
    }

    setIsSupplierSelectBottomSheetOpen(false);
  };

  const selectSupplier = (supplier: Supplier) => {
    setSupplier(supplier);
    setIsSupplierSelectBottomSheetOpen(false);
  };


  return (
    <>
      <DialogHeader>
        <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
          <Typography variant="h6" fontWeight={600}>
            {existingLink ? 'Edit supplier link' : 'Add supplier to product'}
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={onClose}
            sx={{ display: 'flex', p: 0.5, border: 0, bgcolor: 'transparent', cursor: 'pointer' }}
          >
            <Close />
          </Box>
        </Stack>
      </DialogHeader>

      <DialogContent>
        <Stack spacing={4} p={2}>

          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 4, backgroundColor: theme.palette.background.default }}>
            <Typography variant="subtitle1" color="text.primary">
              {supplier?.name}
            </Typography>

            {!existingLink ? (
              <Button variant="text" color="primary" onClick={() => setIsSupplierSelectBottomSheetOpen(true)}>
                Change supplier
              </Button>
            ) : null}
          </Stack>

          <Stack spacing={1}>

            <Typography variant="body2" color="text.primary">
              Purchase price
            </Typography>

            <Stack spacing={1} direction="row" alignItems="center" flexWrap="wrap">

              <Select
                size="small"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                sx={{ flex: 1, minWidth: 120, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
              >
                {currencies.map(currency => (
                  <MenuItem key={currency} value={currency}>
                    {currency}
                  </MenuItem>
                ))}
              </Select>
              <TextField
                size="small"
                type="number"
                value={purchasePrice}
                onChange={e => setPurchasePrice(e.target.value)}
                inputProps={{ step: 0.01, min: 0 }}
                placeholder="0"
                sx={{ flex: 1, minWidth: 120, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography variant="body2" color="text.secondary">
                        {currencySymbol(currency)}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Stack>

          <Stack spacing={1}>

            <Typography variant="body2" color="text.primary">
              Minimum order quantity
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              <ToggleButtonGroup
                exclusive
                size="small"
                value={minimumOrderQuantityType}
                onChange={(_, value: MinimumOrderQuantityType | null) => {
                  if (!value) return;
                  if (value === 'items') {
                    setMinimumOrderQuantityType(value);
                    setMinimumOrderQuantity(coerceMoqToItemsOnTypeSwitch(minimumOrderQuantity));
                  } else {
                    setMinimumOrderQuantityType(value);
                  }
                }}
                aria-label="MOQ unit"
              >
                <ToggleButton value="items" aria-label="Items">
                  <Typography variant="caption" component="span">
                    Items
                  </Typography>
                </ToggleButton>
                <ToggleButton value="currency" aria-label="Currency amount">
                  <Typography variant="caption" component="span">
                    {currency || DISPLAY_CURRENCY}
                  </Typography>
                </ToggleButton>
              </ToggleButtonGroup>
              <TextField
                size="small"
                type="number"
                value={minimumOrderQuantity}
                onChange={e => {
                  const raw = e.target.value;
                  setMinimumOrderQuantity(
                    minimumOrderQuantityType === 'items' ? sanitizeMoqItemsInput(raw) : raw,
                  );
                }}
                inputProps={{
                  step: minimumOrderQuantityType === 'currency' ? 0.01 : 1,
                  min: 0,
                  ...(minimumOrderQuantityType === 'items' ? { inputMode: 'numeric' as const } : {}),
                }}
                placeholder="0"
                sx={{ flex: 1, minWidth: 120, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                InputProps={
                  minimumOrderQuantityType === 'currency'
                    ? {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography variant="body2" color="text.secondary">
                            {sym}
                          </Typography>
                        </InputAdornment>
                      ),
                    }
                    : {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography variant="caption" color="text.secondary">
                            items
                          </Typography>
                        </InputAdornment>
                      ),
                    }
                }
              />
            </Stack>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="body2" color="text.primary">
              Order in multiples of
            </Typography>
            <TextField
              size="small"
              type="number"
              value={orderInMultiplesOf}
              onChange={e => setOrderInMultiplesOf(e.target.value)}
              inputProps={{ step: 1, min: 0 }}
              placeholder="1"
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
            />
          </Stack>

        </Stack>
      </DialogContent>

      <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 3, justifyContent: 'flex-end' }}>
        <Button
          onClick={() => void handleSubmit()}
          variant="contained"
          startIcon={<Add />}
          disabled={!supplier?.id || !product?.id || isLinkingProduct}
        >
          {isLinkingProduct
            ? existingLink
              ? 'Saving...'
              : 'Linking...'
            : existingLink
              ? 'Save'
              : 'Link supplier'}
        </Button>
      </DialogActions>


      <BottomSheet open={isSupplierSelectBottomSheetOpen} onClose={closeSupplierSelectBottomSheet} size={BottomSheetSize.MD}>
        <SupplierSelectBottomSheet onSelect={selectSupplier} onClose={closeSupplierSelectBottomSheet} linkedToProductId={product.id} />
      </BottomSheet >
    </>
  );
};

export default AddSupplierToProductSheet;
